import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Last-resort notification for errors no feature happens to handle itself
 * (a silent `error: () => {}`, or no error callback at all). Feature-level
 * snack bars still win visually - they run downstream of this catchError in
 * the same synchronous notification, so MatSnackBar's open() just replaces
 * this generic one before the user notices it.
 */
function showGlobalErrorToast(snackBar: MatSnackBar, error: HttpErrorResponse): void {
  if (error.status === 0) {
    snackBar.open('Network error. Check your connection and try again.', 'Dismiss', { duration: 5000 });
  } else if (error.status >= 500) {
    snackBar.open('Something went wrong on our end. Please try again.', 'Dismiss', { duration: 5000 });
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  const isAuthEndpoint = req.url.startsWith('/api/auth/');
  const token = authService.accessToken();

  const authorizedReq = token && !isAuthEndpoint ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authorizedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthEndpoint && authService.isAuthenticated()) {
        return authService.refresh().pipe(
          switchMap(() => {
            const retriedReq = req.clone({
              setHeaders: { Authorization: `Bearer ${authService.accessToken()}` },
            });
            return next(retriedReq);
          }),
          catchError((refreshError) => {
            authService.logout();
            router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
            return throwError(() => refreshError);
          })
        );
      }
      if (error.status === 401 && !isAuthEndpoint) {
        authService.logout();
        router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
      } else if (!isAuthEndpoint) {
        showGlobalErrorToast(snackBar, error);
      }
      return throwError(() => error);
    })
  );
};
